import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Search, Trash2, AlertTriangle } from 'lucide-react';

const STATUS_OPTIONS = ['completed', 'pending', 'rejected', 'failed', 'approved'];

export const AdminTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // bulk delete state
  const [showBulk, setShowBulk] = useState(false);
  const [mode, setMode] = useState('date'); // 'date' | 'month'
  const [beforeDate, setBeforeDate] = useState('');
  const [beforeMonth, setBeforeMonth] = useState('');
  const [beforeYear, setBeforeYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    adminAPI.getTransactions(0, 200)
      .then((res) => setTransactions(res.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  const filtered = transactions.filter((t) =>
    !search || t.type?.includes(search.toLowerCase()) || t.currency?.toLowerCase().includes(search.toLowerCase())
  );

  const buildParams = () => {
    const p = {};
    if (mode === 'date' && beforeDate) p.before_date = beforeDate;
    if (mode === 'month' && beforeMonth && beforeYear) {
      p.before_month = beforeMonth;
      p.before_year = beforeYear;
    }
    if (statusFilter) p.status_in = statusFilter;
    return p;
  };

  const runPreview = async () => {
    const p = buildParams();
    if (!p.before_date && !p.before_month) {
      toast.error('Choose a cutoff first');
      return;
    }
    try {
      const res = await adminAPI.previewBulkDeleteTransactions(p);
      setPreviewCount(res.data.count);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
  };

  const runDelete = async () => {
    if (confirmText !== 'DELETE') { toast.error('Type DELETE to confirm'); return; }
    const p = buildParams();
    const payload = { ...p };
    if (payload.status_in) payload.status_in = payload.status_in.split(',');
    setDeleting(true);
    try {
      const res = await adminAPI.bulkDeleteTransactions(payload);
      toast.success(`${res.data.deleted} transactions deleted`);
      setShowBulk(false);
      setPreviewCount(null);
      setConfirmText('');
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed'); }
    finally { setDeleting(false); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-transactions-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Transactions</h1>
            <p className="mt-2 text-muted-foreground">Complete platform transaction history</p>
          </div>
          <Button variant="outline" className="text-red-600 border-red-500/30" onClick={() => { setShowBulk(true); setPreviewCount(null); setConfirmText(''); }} data-testid="bulk-delete-btn">
            <Trash2 className="h-4 w-4 mr-2" /> Bulk delete old transactions
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Transactions ({filtered.length})</CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9 w-64" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((t) => (
                    <TableRow key={t.id} data-testid={`tx-row-${t.id}`}>
                      <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                      <TableCell className="capitalize">{t.type}</TableCell>
                      <TableCell className="font-semibold">{t.amount} {t.currency}</TableCell>
                      <TableCell className="text-xs max-w-[240px] truncate" title={t.reference}>{t.reference || '-'}</TableCell>
                      <TableCell>
                        <Badge className={t.status === 'completed' || t.status === 'approved' ? 'bg-green-600' : t.status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'}>
                          {t.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bulk delete dialog */}
      <Dialog open={showBulk} onOpenChange={(v) => { setShowBulk(v); if (!v) { setPreviewCount(null); setConfirmText(''); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Delete old transactions</DialogTitle>
            <DialogDescription>
              This permanently removes transactions from the database. Wallet balances are NOT recomputed — only history rows are dropped. Use for cleaning very old month-end noise.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="date" data-testid="bulk-mode-date">By date</TabsTrigger>
              <TabsTrigger value="month" data-testid="bulk-mode-month">By month</TabsTrigger>
            </TabsList>
            <TabsContent value="date" className="space-y-2 mt-4">
              <Label>Delete transactions BEFORE this date</Label>
              <Input type="date" value={beforeDate} onChange={(e) => { setBeforeDate(e.target.value); setPreviewCount(null); }} data-testid="bulk-before-date" />
            </TabsContent>
            <TabsContent value="month" className="space-y-2 mt-4">
              <Label>Delete transactions BEFORE the start of this month</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={String(beforeMonth)} onValueChange={(v) => { setBeforeMonth(v); setPreviewCount(null); }}>
                  <SelectTrigger data-testid="bulk-before-month"><SelectValue placeholder="Month" /></SelectTrigger>
                  <SelectContent>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" value={beforeYear} onChange={(e) => { setBeforeYear(e.target.value); setPreviewCount(null); }} placeholder="Year" data-testid="bulk-before-year" />
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-3 space-y-1">
            <Label>Only delete transactions with these statuses (comma-separated, optional)</Label>
            <Input value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPreviewCount(null); }} placeholder="e.g. completed,rejected" data-testid="bulk-status-filter" />
            <p className="text-xs text-muted-foreground">Valid: {STATUS_OPTIONS.join(', ')}. Leave blank to include ALL statuses.</p>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button variant="outline" onClick={runPreview} data-testid="bulk-preview-btn">Preview count</Button>
            {previewCount !== null && (
              <p className={`text-sm font-medium ${previewCount === 0 ? 'text-muted-foreground' : 'text-red-600'}`} data-testid="bulk-preview-count">
                {previewCount} transaction(s) will be deleted
              </p>
            )}
          </div>

          <div className="mt-3 space-y-1">
            <Label>Type <code className="text-red-600">DELETE</code> to confirm</Label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" data-testid="bulk-confirm-input" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            <Button variant="destructive" onClick={runDelete} disabled={deleting || confirmText !== 'DELETE'} data-testid="bulk-confirm-delete">
              <Trash2 className="h-4 w-4 mr-2" /> {deleting ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
